import { useGetSellables } from "@/api/artwork/artwork";
import { BriefSellable, PagingParameters, SellableItemType } from "@/api/dtos";
import { ArticleEditDrawer } from "@/components/cms/e-shop/ArticleEditDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { DatatablePagination } from "@/components/ui/datatable-pagination";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { generateSortableHeader } from "@/components/ui/shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AddArticlePage = () => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const columns: ColumnDef<BriefSellable>[] = [
    {
      id: "recordingNumber",
      size: 1,
      header: generateSortableHeader(t("eshop.items.table.recordingNumber")),
      cell: ({ row }) => (
        <>
          <span className="font-medium inline-block w-fit text-nowrap">
            {row.original.recordingNumber.toString()}
          </span>
        </>
      ),
    },
    {
      id: "title",
      header: generateSortableHeader(t("eshop.items.table.title")),
      enableResizing: true,
      size: 10000,
      cell: ({ row }) => (
        <div className="px-3 flex flex-row gap-3 items-center">
          {row.original.previews && row.original.previews.length > 0 ? (
            <img
              alt={`Thumbnail of ${row.original.title}`}
              className="size-10 aspect-square object-contain"
              src={row.original.previews[0].small ?? ""}
            ></img>
          ) : (
            <div className="size-10 bg-slate-500"></div>
          )}
          <span className="font-medium">{row.original.title as string}</span>
        </div>
      ),
    },
    {
      size: 1,
      id: "type",
      header: generateSortableHeader(t("eshop.addItem.table.type")),
      cell: ({ row }) =>
        row.original.type && (
          <Badge variant="outlined" className="font-medium text-xs text-nowrap">
            {row.original.type as string}
          </Badge>
        ),
    },
    {
      header: "",
      id: "edit",
      size: 1,
      cell: ({ row }) => (
        <ArticleEditDrawer variant="create" sellable={row.original} />
      ),
    },
  ];

  const navigate = useNavigate();

  const [getGlobalSearch, setGlobalSearch] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([]);

  const [sellableItemType, setSellableItemType] =
    useState<SellableItemType>("artwork");
  const { data } = useGetSellables(
    sellableItemType,
    getPagination,
    sorting,
    getGlobalSearch,
  );

  return (
    <div className="py-11 px-24 gap-8 flex flex-col">
      <div className="flex flex-row justify-between">
        <div className="flex-[2]">
          <h1 className="text-3xl font-semibold text-left">
            {t(`addArtworksForSale`)}
          </h1>
        </div>
        <div className="flex flex-row gap-6">
          <Input
            type="text"
            placeholder={t("search")}
            className="min-w-72"
            value={getGlobalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
          />
          <Button onClick={() => navigate("/cms/e-shop")}>{t("finish")}</Button>
        </div>
      </div>
      <Separator />
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-start">
          <Tabs
            value={sellableItemType}
            onValueChange={(v) => setSellableItemType(v as SellableItemType)}
          >
            <TabsList>
              <TabsTrigger
                value="artwork"
                className="flex items-center gap-2 text-xs"
              >
                {t("eshop.addItem.artwork.tab")}
              </TabsTrigger>
              <TabsTrigger
                value="multipleprint"
                className="flex items-center gap-2 text-xs"
              >
                {t("eshop.addItem.multipleprint.tab")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <DataTable
          columns={columns}
          data={data ? data.data : []}
          options={{
            state: {
              sorting: sorting,
            },
            onSortingChange: setSorting,
            manualSorting: true,
          }}
        ></DataTable>
      </div>
      <div>
        <DatatablePagination
          pagination={getPagination}
          setPage={setPagination}
          totalSize={data?.totalSize ?? 0}
        />
      </div>
    </div>
  );
};

export default AddArticlePage;
